import { useEffect, useRef } from 'react';

const MODAL_HISTORY_KEY = '__modal';

/**
 * Pushes a history entry while `open` so the device/browser back action closes the overlay
 * instead of leaving the page. Stacked overlays each push their own entry (back closes top-first).
 */
export function useCloseOnBack(open: boolean, onClose: () => void, enabled = true) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !enabled) return;

    const openedAtPath = window.location.pathname;
    let closedByBack = false;
    let ignoreNextPop = false;

    window.history.pushState({ [MODAL_HISTORY_KEY]: true }, '');

    const onPopState = () => {
      if (ignoreNextPop) {
        ignoreNextPop = false;
        return;
      }
      closedByBack = true;
      onCloseRef.current();
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      const navigatedAway = window.location.pathname !== openedAtPath;
      if (!closedByBack && !navigatedAway) {
        ignoreNextPop = true;
        window.history.back();
      }
      window.removeEventListener('popstate', onPopState);
    };
  }, [open, enabled]);
}
